<template>
  <span class="rd-wrap">
    <button class="rd-btn" type="button" @click.stop="toggle">Report details</button>
    <div class="rd-panel" :class="{ open }">
      <div class="rd-row">
        <span class="rd-k">Report ID</span>
        <span class="rd-v">{{ reportId }}</span>
      </div>
      <div class="rd-row">
        <span class="rd-k">Time</span>
        <span class="rd-v">{{ timeLabel }}</span>
      </div>
      <div class="rd-row">
        <span class="rd-k">Slug</span>
        <a class="rd-v rd-link" :href="url || '#'" @click.prevent="openUrlInNewTab(url)">{{ slugLabel }}</a>
      </div>
      <div v-if="secretKey" class="rd-row">
        <span class="rd-k">Secret key</span>
        <span class="rd-v rd-mono">{{ secretKey }}</span>
        <button
          class="rd-copy"
          type="button"
          title="Copy secret key"
          v-clipboard:copy="secretKey"
          v-clipboard:success="onCopyOk"
          v-clipboard:error="onCopyErr"
        >⧉</button>
      </div>
      <div v-if="secretKey" class="rd-row">
        <span class="rd-k">Content editor</span>
        <a
          class="rd-v rd-link rd-mono"
          :href="contentEditorUrl || '#'"
          @click.prevent="openContentEditor"
        >{{ contentEditorUrl }}</a>
        <button
          class="rd-copy"
          type="button"
          title="Copy content editor URL"
          v-clipboard:copy="contentEditorUrl"
          v-clipboard:success="onCopyOk"
          v-clipboard:error="onCopyErr"
        >⧉</button>
      </div>
      <a
        v-if="secretKey"
        class="rd-open"
        :href="contentEditorUrl || '#'"
        @click.prevent="openContentEditor"
      >Open in content editor →</a>
    </div>
  </span>
</template>

<script>
// "Report details" popover for the AI Writer v2 head (design: specs/2026-07 -
// AI Writer report details). Elements + links intentionally mirror the OLD
// content-brief box (side-pane/ContentBrief/ReportEmbed.vue) — the computeds
// and methods below are copied from there so behavior is identical.
import { getPureServicePkgName } from "@/utils";
import { whiteGlovePlans } from "@/components/plans_constants";

export default {
  name: "AiwV2ReportDetailsPopover",
  props: {
    pageRun: { type: Object, default: () => ({}) },
    // The finished run whose article the content editor should pre-load (Publish
    // step passes session.aiRunId). Needed so WG/mission-control opens THIS run's
    // article, not the latest AIRun for the page (a "start over" mints a new one).
    aiRunId: { default: null },
  },
  data() {
    return { open: false };
  },
  computed: {
    reportId() {
      return (this.pageRun && (this.pageRun.id || this.pageRun.reportId)) || "";
    },
    createdAt() {
      return (this.pageRun && (this.pageRun.createdAt || this.pageRun.created_at)) || "";
    },
    url() {
      return (this.pageRun && this.pageRun.url) || "";
    },
    secretKey() {
      return (this.pageRun && this.pageRun.secretKey) || "";
    },
    pageNotBuilt() {
      return !!(this.pageRun && this.pageRun.pageNotBuilt);
    },
    timeLabel() {
      return this.getDateAsLocalString(this.createdAt);
    },
    slugLabel() {
      return this.getSubFolderPartFromUrl(this.url);
    },
    // ReportEmbed.vue:1843-1850 — base secret-key editor URL.
    uniqueUrlForEditor() {
      if (!this.secretKey) return "";
      const routeData = this.$router.resolve({
        name: "FullPageSourceEditor",
        query: { secretKey: this.secretKey },
      });
      return window.location.origin + "/" + routeData.href;
    },
    // Content-editor URL for the finished article. Carries the secret-key base +
    // siloKeywordId/relId, and — for White Glove / mission-control — the AI-writer
    // params that make the editor PRE-LOAD the article. The backend
    // (get_edited_html_source) only seeds the editor body from the AI run when
    // fromAiWriter=true; without it the editor opens empty. Mirrors the old
    // content-brief box (ReportEmbed.openUrlInNewTab). orderedReportType selects
    // the Human-edited draft column.
    contentEditorUrl() {
      const base = this.uniqueUrlForEditor;
      if (!base) return "";
      const q = this.$route.query;
      let url = base; // …#/content-editor?secretKey=X
      if (q.siloKeywordId) url += `&siloKeywordId=${q.siloKeywordId}`;
      const rel = this.$store.state.selectedSubAccountRelation;
      if (rel) url += `&relId=${rel.id}`;
      if (this.isWhiteGloveUser || this.isMissionControlSession) {
        url += `&fromAiWriter=true&isWhiteGloveUser=true`;
        const aiRunId = this.aiRunId || q.aiRunId;
        if (aiRunId) url += `&aiRunId=${aiRunId}`;
        if (q.isOrderedReport === "true" && q.orderedReportType) {
          url += `&isOrderedReport=true&orderedReportType=${q.orderedReportType}`;
        }
      }
      return url;
    },
    // ReportEmbed.vue:1868-1874
    isWhiteGloveUser() {
      const user = this.$store.state.user;
      if (!user) return false;
      const rel = this.$store.state.selectedSubAccountRelation;
      const accountType = rel ? rel.ownerAccountType : user.accountType;
      return whiteGlovePlans.includes(getPureServicePkgName(accountType));
    },
    isMissionControlSession() {
      const rel = this.$store.state.selectedSubAccountRelation;
      return !!(rel && rel.isMissionControlSubAccount);
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick);
  },
  methods: {
    toggle() {
      this.open = !this.open;
    },
    onDocClick(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) this.open = false;
    },
    onCopyOk() {
      this.$notify({ group: "info", type: "success", title: "Alert", text: "Copied to clipboard." });
    },
    onCopyErr() {
      this.$notify({ group: "info", type: "error", title: "Alert", text: "Could not copy. Please try again." });
    },
    // ReportEmbed.vue:2121-2123
    getDateAsLocalString(dateStr) {
      if (!dateStr) return "";
      const s = typeof dateStr === "string" ? dateStr.replace(" ", "T") + "Z" : dateStr;
      const d = new Date(s);
      return isNaN(d.getTime()) ? "" : d.toLocaleString();
    },
    // ReportEmbed.vue:2124-2138
    getSubFolderPartFromUrl(url) {
      if (this.pageNotBuilt) return "N / A";
      if (!url || url.length <= 8) return "";
      const idx = url.indexOf("/", 8);
      const rlt = url.substr(idx).trim();
      if (idx === -1 || rlt === "/" || rlt === "") return "Homepage";
      return rlt;
    },
    // ReportEmbed.vue:2139-2170 — keeps the white-glove/mission-control URL
    // decoration so links open with the same context params as the old box.
    openUrlInNewTab(url) {
      if (!url) return;
      let redirectUrl = url;
      if (this.isWhiteGloveUser || this.isMissionControlSession) {
        const aiRunId = this.$route.query.aiRunId;
        const isOrderedReport = this.$route.query.isOrderedReport === "true";
        const siloKeywordId = this.$route.query.siloKeywordId;
        const orderedReportType = this.$route.query.orderedReportType;
        const queryParams = ["fromAiWriter=true"];

        queryParams.push("isWhiteGloveUser=true");

        if (aiRunId) {
          queryParams.push(`aiRunId=${aiRunId}`);
        }

        if (siloKeywordId && isOrderedReport && orderedReportType) {
          queryParams.push(`orderedReportType=${orderedReportType}`);
        }

        if (this.$store.state.selectedSubAccountRelation) {
          queryParams.push(`relId=${this.$store.state.selectedSubAccountRelation.id}`);
        }

        if (siloKeywordId) {
          queryParams.push(`siloKeywordId=${siloKeywordId}`);
        }

        const separator = url.includes("?") ? "&" : "?";
        redirectUrl = `${url}${separator}${queryParams.join("&")}`;
      }
      window.open(redirectUrl, "_blank");
    },
    // The content-editor URL is already fully built (contentEditorUrl) — open it
    // directly, like the live gotoContentEditor(), without extra decoration.
    openContentEditor() {
      if (this.contentEditorUrl) window.open(this.contentEditorUrl, "_blank");
    },
  },
};
</script>

<style scoped>
/* Report details popover — ported from the design spec (.rd-*). */
.rd-wrap { position: relative; display: inline-flex; }
.rd-btn {
  background: none; border: none; padding: 0;
  font-family: inherit; font-size: inherit;
  color: var(--pop-orange-deep, #b23d00); font-weight: 700;
  cursor: pointer; text-decoration: underline; text-underline-offset: 2px;
}
.rd-panel {
  display: none; position: absolute; top: 24px; left: 0; width: 430px; max-width: 90vw;
  background: #fff; border: 1px solid var(--border, #e4e4e4);
  border-radius: var(--r-lg, 10px); box-shadow: var(--shadow-lg, 0 10px 36px rgba(15, 20, 25, .08));
  padding: 14px 16px; z-index: 80; text-align: left;
}
.rd-panel.open { display: block; }
.rd-row {
  display: grid; grid-template-columns: 96px 1fr auto; gap: 10px; align-items: baseline;
  padding: 6px 0; border-bottom: 1px solid var(--border, #e4e4e4);
}
.rd-row:last-of-type { border-bottom: none; }
.rd-k {
  font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase;
  color: var(--text-3, #6b7b7e);
}
.rd-v { font-size: 12.5px; color: var(--text, #052e33); font-weight: 600; overflow-wrap: anywhere; }
.rd-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; font-weight: 500; }
.rd-link { color: var(--pop-blue, #316bff); text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
.rd-copy {
  background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: 6px;
  width: 24px; height: 24px; cursor: pointer; color: var(--text-3, #6b7b7e);
  font-size: 12px; line-height: 1; font-family: inherit;
}
.rd-copy:hover { color: var(--text, #052e33); border-color: var(--border-strong, #ccc); }
/* The spec reuses its prototype .btn.btn-soft.btn-sm here; style the soft button directly. */
.rd-open {
  margin-top: 11px; display: inline-flex; align-items: center; text-decoration: none;
  padding: 6px 12px; font-size: 12px; font-weight: 700;
  color: var(--text-2, #014751); background: var(--surface-2, #f7f7f7);
  border: 1px solid var(--border, #e4e4e4); border-radius: var(--r, 8px);
}
.rd-open:hover { border-color: var(--border-strong, #ccc); color: var(--text, #052e33); }
</style>
